import {SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {INCOME_INFLUENCE_PATH_LENGTH} from "../../../configs/income";
import {getHexDistance} from "../../coords/getHexDistance";
import {findOwner} from "../findOwner";
import {getEnemyCitiesForPlayer} from "../getEnemyCitiesForPlayer";
import {calcGuildIncome} from "./calcGuildIncome";
import {calcTempleIncome} from "./calcTempleIncome";

const CALC_INCOME_BY_SPECIAL_BUILDING = {
  [SPECIAL_BUILDING_TOKENS.GUILD]: calcGuildIncome,
  [SPECIAL_BUILDING_TOKENS.TEMPLE]: calcTempleIncome,
};

/**
 * Гильдии/храмы (любого игрока), чей доход изменился из-за размещения нового
 * строения на ячейке placedHexKey. Гильдия/храм дальше INCOME_INFLUENCE_PATH_LENGTH
 * ячеек по прямой от placedHexKey затронута быть не может: путь влияния — БФС
 * не длиннее INCOME_INFLUENCE_PATH_LENGTH шагов по соседним ячейкам, а число
 * таких шагов не может быть меньше прямого гексагонального расстояния.
 *
 * @param {string} placedHexKey - ячейка только что размещенного строения
 * @param {Object} hexesBefore - hexes ДО размещения строения
 * @param {Object} hexesAfter - hexes ПОСЛЕ размещения строения
 * @param {Object} specialBuildings - плоская карта {hexKey: buildingType}
 * @param {Array} players
 * @param {Object} citiesCacheBefore - города, пересчитанные из hexesBefore
 *   (НЕ store-кэш state.shared.gameState.citiesCache — тот обновляется только
 *   на COMPLETE_PHASE и внутри одного хода строительства не видит города,
 *   расширенные более ранними действиями этого же хода)
 * @param {Object} citiesCacheAfter - города, пересчитанные из hexesAfter
 * @param {Object} [options]
 * @param {string[]} [options.changedHexKeys] - все ячейки, где hexesBefore и
 *   hexesAfter различаются (по умолчанию — только placedHexKey). Отсев по
 *   расстоянию идет от ближайшей из них: на поле «с планом» вместе с чужой
 *   стройкой меняются и ячейки снятого ею плана
 * @returns {Array<{hexKey: string, buildingType: string}>}
 */
export const getGuildsAndTemplesWithChangedIncome = (
  placedHexKey, hexesBefore, hexesAfter, specialBuildings, players, citiesCacheBefore, citiesCacheAfter,
  {changedHexKeys = [placedHexKey]} = {},
) => {
  const affected = [];

  for (const [hexKey, buildingType] of Object.entries(specialBuildings)) {
    if (hexKey === placedHexKey) continue;

    const calcIncome = CALC_INCOME_BY_SPECIAL_BUILDING[buildingType];
    if (!calcIncome) continue;

    if (changedHexKeys.every((changedHexKey) => getHexDistance(changedHexKey, hexKey) > INCOME_INFLUENCE_PATH_LENGTH)) continue;

    const owner = findOwner(hexKey, players, citiesCacheAfter);
    if (!owner) continue;

    const enemyCitiesBefore = getEnemyCitiesForPlayer(owner.color, citiesCacheBefore);
    const enemyCitiesAfter = getEnemyCitiesForPlayer(owner.color, citiesCacheAfter);
    const incomeBefore = calcIncome(hexKey, owner, enemyCitiesBefore, hexesBefore);
    const incomeAfter = calcIncome(hexKey, owner, enemyCitiesAfter, hexesAfter);

    if (incomeBefore !== incomeAfter) {
      affected.push({hexKey, buildingType});
    }
  }

  return affected;
};
